import { Tabs, theme } from 'antd'

import { classNames } from '@/Ship/Helpers/classNames'

import type { TabsProps } from 'antd'
import type { CSSProperties, ReactNode } from 'react'

import styles from './EntityCard.module.css'

/** Кольори картки — з токенів antd, щоб тема мінялася в одному місці. */
export const useEntityCardPalette = (): CSSProperties => {
    const { token } = theme.useToken()

    return {
        '--ec-surface': token.colorBgContainer,
        '--ec-text': token.colorText,
        '--ec-muted': token.colorTextSecondary,
        '--ec-accent': token.colorPrimary,
        '--ec-divider': token.colorSplit,
        '--ec-hover': token.colorPrimaryBg,
    } as CSSProperties
}

/**
 * Корінь картки сутності.
 *
 * Правила вигляду, спільні для всіх карток (зразок — картка компанії, який
 * затвердив замовник; в Астеріксі за ним зроблено картки клієнта, перевізника
 * й справи):
 *
 *  - **шапка** — номер, статус, ключові позначки й назва: те, що читають
 *    першим. Дублювати в шапці поле з блоку нормально — шапка зводить,
 *    блоки дають дані;
 *  - **вкладки** — у тому ж білому блоці, що й шапка; перша — «Загальне»
 *    з полями, далі пов'язане (підлеглі записи, «Історія змін»);
 *  - **блоки** — білі плашки без рамок на сірому тлі, жирний заголовок без
 *    іконки. Межа між групами видна з першого погляду;
 *  - **поля** — двома стовпцями, підпис дрібно великими літерами, лінія під
 *    кожним. Назва й довгі тексти — на всю ширину;
 *  - **групи — за питанням, на яке відповідають**: що, хто, коли. Службове
 *    (хто й коли створив і змінив) — окремим блоком останнім;
 *  - **праворуч — стрічка** (коментарі, події), якщо вона в сутності є;
 *  - **форма створення — тієї ж геометрії**: поля там само, де їх потім
 *    шукатимуть. Службових полів у ній немає, а на місці стрічки —
 *    пояснення, коли вона з'явиться.
 */
export const EntityLayout = ({ children }: { children: ReactNode }) => {
    const palette = useEntityCardPalette()

    return (
        <div className={classNames(styles['layout'])} style={palette}>
            {children}
        </div>
    )
}

export interface EntityTabsProps {
    /** Шапка картки — над смугою вкладок, в одному білому блоці з нею */
    header?: ReactNode
    items: NonNullable<TabsProps['items']>
    onChange?: (key: string) => void
}

/** Вкладки картки: смуга — нижній край шапки, вміст — на сірому тлі. */
export const EntityTabs = ({ header, items, onChange }: EntityTabsProps) => (
    <Tabs
        items={items}
        {...(onChange ? { onChange } : {})}
        renderTabBar={(props, DefaultTabBar) => (
            <div className={classNames(styles['hero'], styles['heroWithTabs'])}>
                {header}
                <DefaultTabBar {...props} />
            </div>
        )}
    />
)

/** Шапка картки без вкладок — той самий білий блок. */
export const EntityHero = ({ children }: { children: ReactNode }) => (
    <div className={classNames(styles['hero'])}>{children}</div>
)

export interface EntityBodyProps {
    /** Блоки з даними */
    main: ReactNode
    /** Стрічка праворуч; без неї блоки займають усю ширину */
    aside?: ReactNode
}

/**
 * Тіло картки: дані ліворуч (3/5), стрічка праворуч (2/5).
 *
 * На вузькій картці стовпці стають один під одним — ширину міряємо від самої
 * картки, а не від вікна, бо шухляда буває вужчою за екран.
 */
export const EntityBody = ({ main, aside }: EntityBodyProps) => (
    <div className={classNames(styles['body'])}>
        <div className={classNames(styles['bodyGrid'], aside !== undefined && styles['bodySplit'])}>
            <div className={classNames(styles['column'])}>{main}</div>
            {aside !== undefined ? <div className={classNames(styles['column'])}>{aside}</div> : null}
        </div>
    </div>
)

/** Вміст вкладки, який сам не ділиться на блоки: реєстр, дерево, журнал. */
export const EntityPanel = ({ children }: { children: ReactNode }) => (
    <div className={classNames(styles['panel'])}>{children}</div>
)
